/**
 * The shape of `/api/roadmap` — the artifact `scripts/roadmap_build.py` writes to
 * `orchestration/functions/data/roadmap.json`.
 *
 * These types are hand-written rather than generated, and the reason matters for anyone
 * tempted to "tidy" them: almost every field here is OPTIONAL because the generator's
 * absence-is-not-a-zero rule means a lane, a source or a reading can legitimately be
 * missing, and a missing reading must render as *unmeasured*, never as zero. A type that
 * declared `rows: number` would push the renderer toward `rows ?? 0`, which is the exact
 * confident-negative this whole page exists to prevent.
 *
 * `schemaVersion` is checked at render time for the same reason. A payload from a newer
 * generator is not silently rendered with fields dropped — it says so.
 */

/** How a single input file read. `read` is the only state that carries content. */
export interface SourceReading {
  name: string;
  path: string;
  state: 'read' | 'absent' | 'unreadable';
  /** First 12 hex of the sha256 of the bytes actually read. Present only when `read`. */
  sha256?: string;
  bytes?: number;
  detail?: string;
}

/** A lane's own reading. `state` is never inferred from whether `rows` is present. */
export interface LaneReading {
  state: 'read' | 'absent' | 'unreadable' | 'unmeasured';
  rows?: number;
  detail?: string;
  /** Which ROADMAP_APP.md phase will populate this lane, when it is not populated yet. */
  phase?: string;
}

export interface RoadmapLanes {
  claimed: {
    counts: LaneReading;
    remaining: LaneReading;
    /** Optional: added with the spine. A Phase-3 artifact has neither. */
    capabilities?: LaneReading;
    products?: LaneReading;
    arc?: LaneReading;
  };
  planned: { northStar: LaneReading; queue: LaneReading };
  shipped: LaneReading;
  deployed: LaneReading;
  verified: LaneReading;
}

/**
 * One artifact a document cites, resolved against the repo tree.
 *
 * `unresolved` is deliberately NOT `absent`. Three of NORTH-STAR §3's rows point at
 * `cds-mcp-servers`, a sibling repo that is not checked out beside this one, so a path that
 * does not resolve here is most often a checkout boundary rather than a missing artifact.
 * Collapsing the two would manufacture an overclaim out of a clone layout.
 */
export interface Citation {
  cite: string;
  state: 'resolved' | 'unresolved' | 'glob';
  path?: string | null;
  /** More than one file in the tree matches this suffix, so the citation is imprecise. */
  ambiguous?: boolean;
}

export interface PlannedItem {
  id: string;
  title: string;
  detail?: string;
  source: string;
  /**
   * Artifacts this commitment cites. Rendered beside the expiry because a commitment whose
   * premise is "`gate.sh` has never been committed" is refuted the day that file appears —
   * and nothing re-reads the premise on its own.
   */
  cites?: Citation[];
  planned?: {
    state: string;
    declaredState?: string;
    owner: string;
    owned: boolean;
    reviewBy?: string | null;
    reviewByParsed: boolean;
  };
  claimed?: { state: string };
  verified?: { state: string; check?: string | null };
  /** Set when this §11 row was joined to a §4.1 row by the generator. */
  joinedTo?: string;
}

export interface ClaimedCount {
  label: string;
  claimed: number;
}

export interface DeployedReading {
  state: 'read' | 'absent' | 'unreadable' | 'unmeasured';
  detail?: string;
  /** ISO instant the stamps file was written. Age is the RENDERER's subtraction. */
  stampedAt?: string;
  onRef?: Record<string, boolean>;
  unknown?: string[];
  repos?: Record<string, Record<string, string>>;
}

export interface Join {
  kind: 'exact' | 'drift' | string;
  label?: string;
  itemId?: string;
  detail?: string;
}

export type GapSeverity = 'high' | 'medium' | 'info';

export interface Gap {
  type: string;
  severity: GapSeverity;
  title: string;
  detail: string;
  itemId?: string;
  claimed?: number;
  measured?: number;
  baseline?: number;
  nearest?: string | null;
  nearestValue?: number | null;
  /** `[from, to]` — which two lanes disagree. Written by the generator, not inferred here. */
  lanes?: [string, string];
  /** Why the CLAIMED lane could not be traced to an artifact. */
  trace?: string;
  cites?: Citation[];
}

/**
 * One of the four directed disagreements ROADMAP_APP.md §4 names, plus VERIFIED.
 *
 * `state: 'unmeasured'` with `count: null` is the load-bearing case, and it is why this comes
 * from the generator rather than being derived here by grouping `gaps` on `lanes`. A renderer
 * that grouped a flat list could not tell "no gaps in this direction" from "this direction was
 * never computed" — both are an empty array — and rendering the second as the first is the page
 * claiming an alignment it never checked for.
 */
export interface AlignmentRow {
  id: string;
  from: string;
  to: string;
  title: string;
  meaning: string;
  state: 'measured' | 'unmeasured';
  types: string[];
  /** null when `state` is `unmeasured`. NEVER coalesce this to 0. */
  count: number | null;
  bySeverity: Partial<Record<GapSeverity, number>> | null;
  /** Names the exact input that would make an unmeasured direction measurable. */
  blockedBy?: string;
  /** Gap types this direction owns that only the CLOCK can produce, so the page computes them. */
  renderedClientSide?: string[];
}

/**
 * THE SPINE — the capability story, and the part of this payload a human actually reads.
 *
 * Everything above this point in the file describes the MACHINERY: lanes, gaps, joins,
 * directions. That machinery still runs and still fails the build on an overclaim. What it
 * does on screen is narrower by design: it decides `evidence` below, i.e. whether a
 * capability card is drawn solid or outlined. Nothing else from the lane vocabulary reaches
 * the reader — the words *lane*, *gap* and *unmeasured* do not appear anywhere in
 * `SpinePage.tsx`, and `check-spine.py` asserts that rather than trusting it.
 */

/** A hard number from §7, with §3's recount of it beside it. */
export interface SpineMetric {
  label: string;
  value: number;
  /** null means §7 carries this number and §3 has no recount command for it. */
  baseline: number | null;
  /** null when there is no baseline to agree with. Never collapse null to false. */
  agrees: boolean | null;
}

export interface SpineCapability {
  id: string;
  /** The document's own wording, lower-cased. The join key, not a display field. */
  key: string;
  title: string;
  detail: string;
  /** e.g. `ADP-PLATFORM-OVERVIEW.md §4`. */
  source: string;
  metrics: SpineMetric[];
  /**
   * `proven` = at least one number, every one of them recountable from source and agreeing.
   * `asserted` = everything else: no number, no recount command, or a disagreement.
   *
   * Deliberately one bit. A reader is owed "can this be checked"; a taxonomy of *why* it
   * could not be is the engineering view, and putting it on this page is what the last
   * revision did wrong.
   */
  evidence: 'proven' | 'asserted';
  /** Labels behind the `asserted` tint, for the tooltip. Empty when `proven`. */
  unrecounted: string[];
}

export interface SpineGroup {
  id: string;
  /** The §-number in ADP-PLATFORM-OVERVIEW.md this group was read from. */
  heading: number;
  title: string;
  capabilities: SpineCapability[];
}

/** One of the four sockets a product plugs into the spine through. */
export interface SpineSocket {
  label: string;
  state: 'live' | 'inherited' | 'pending';
  /**
   * Whether §11 says anything about this socket for this product. `cited: false` with
   * `state: 'pending'` means *the document is silent*, which is NOT the same as *the
   * document says it is not done* — and the page draws the two differently, because an
   * absence nobody can distinguish from a measurement is how this page would start lying
   * in the reassuring direction.
   */
  cited: boolean;
  quote: string | null;
}

export interface SpineProduct {
  id: string;
  key: string;
  name: string;
  /** §11's own words: "Furthest along", "Cheapest to light up", "The clean proof". */
  distance: string;
  lead: string;
  detail: string;
  state: string;
  source: string;
  sockets: SpineSocket[];
  attached: number;
  sockets_total: number;
}

export interface Spine {
  /** §11's headline sentence, lifted not paraphrased. The page's one-line thesis. */
  arc: { from: string; to: string; source: string } | null;
  groups: SpineGroup[];
  products: SpineProduct[];
  /** The four items that finish it, by reference to `items.claimedRemaining`. */
  remaining: { id: string; title: string; detail: string }[];
  sources: { capabilities: LaneReading; products: LaneReading };
  summary: {
    capabilities: number;
    proven: number;
    asserted: number;
    products: number;
    remaining: number;
    joinErrors: number;
  };
  /**
   * Always empty in a committed artifact: the generator REFUSES to write when this is
   * non-empty (exit 2), so a payload that reaches this page cannot carry one. It is in the
   * type because the field is in the schema, and because a reader should be able to see
   * that the guarantee is enforced upstream rather than assumed here.
   */
  joinErrors: string[];
}

export interface Roadmap {
  schemaVersion: number;
  sources: SourceReading[];
  lanes: RoadmapLanes;
  /** Optional: a Phase-2/3 artifact has no `spine` key, and the page says so rather than
   *  rendering an empty story that looks like a platform with no capabilities. */
  spine?: Spine;
  items: { planned: PlannedItem[]; claimedRemaining: PlannedItem[] };
  claimedCounts: ClaimedCount[];
  /** NORTH-STAR §3 rows, keyed by lower-cased label. The check side of a claimed count. */
  baselines: Record<string, number>;
  deployed: DeployedReading;
  shippedCount: number;
  joins: Join[];
  gaps: Gap[];
  /** Optional: a Phase-2 artifact has no `alignment` key, and the page renders without it. */
  alignment?: AlignmentRow[];
  summary: {
    /** Gap types no §4 direction owns — a count that would otherwise not add up on screen. */
    unroutedGapTypes?: string[];
    plannedItems: number;
    claimedRemainingItems: number;
    claimedCounts: number;
    shippedChanges: number;
    gaps: number;
    gapsBySeverity: Partial<Record<GapSeverity, number>>;
    lanesUnmeasured: string[];
  };
}

/** The version this renderer was written against. */
export const ROADMAP_SCHEMA_VERSION = 1;
